import { AfterViewInit, Component, EventEmitter, Inject, isDevMode, OnDestroy, Output } from '@angular/core';
import { finalize, first, forkJoin, Observable, Subject, Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import PaymentData = GooglePay.PaymentData;
import { PaymentProviderComponentInterface } from '../payment-provider.component.interface';
import { GooglePay } from '../gpay/client/client';
import { OrderDataProvider } from 'libs/core/src/lib/data-provider/order.data-provider';
import { PaymentConfigViewModel } from 'libs/core/src/lib/model/view-model/payment/config/payment.config.view.model';
import { OrderStateService } from 'libs/core/src/lib/state/order.state.service';
import { PaymentProviderActionType, PaymentProviderStateService } from '../../../service/payment-provider-state.service';
import {
  PaymentProviderEvent,
  DonePaymentProviderEvent,
  ErrorPaymentKeyProviderEvent,
  ErrorPaymentProviderEvent,
  WorkPaymentProviderEvent,
  WaitPaymentProviderEvent
} from '../../event/payment-provider.event';
import { OrderPaymentPackage } from '../../../../../../../../core/src/lib/model/order-payment.package.model';
import { CreatePaymentDataRequestDto, GooglePayService } from '../gpay/service/google-pay.service';
import {
  ENVIRONMENT_TOKEN,
  FinancialHelper,
  LoaderService,
  loadScript,
  loadScriptPromise,
  makeUrl,
  MobileDeviceUtils,
  ProviderEnum,
  sanitizeInput
} from 'libs/core/src/public-api';
import { PaymentProviderPayMethodViewModel } from 'libs/core/src/lib/model/view-model/payment-provider-pay-method.view.model';
import { PaymentViewModel } from 'libs/core/src/lib/model/view-model/payment.view.model';
import { OrderViewModel } from 'libs/core/src/lib/model/view-model/order/order.view.model';
import { ApplePayService } from '../apay/service/apple-pay.service';
import { FormGroup } from '@angular/forms';
import { CreatePaymentRedirectUrl } from '../../../create-payment-redirect-url';
import { ActivatedRoute } from '@angular/router';
import { PaymentHelperService } from '../../../service/payment-helper.service';
import { PaymentMethodEnum } from '../../model/payment-method.enum';
import { DateTime } from 'luxon';
import { PaymentAuthMethod, PaymentCardNetwork, PaymentDataModel, PaymentTotalPriceStatus } from '../gpay/model/payment-data.model';
import { generalPaymentError, przelewy24PaymentError } from '../../../payment-error.consts';
import { P24CardMetadata, P24MessageData, P24TokenizationData } from './model/p24.interface';
import { AuthStateService } from 'libs/core/src/lib/state/auth.state.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoggerService } from 'libs/core/src/lib/service/logger/logger.service';
import { OrderPaymentRequestModel } from 'libs/core/src/lib/model/request/order-payment.request.model';
import { TranslateService } from '@ngx-translate/core';
import { LoaderEnum } from 'libs/core/src/lib/enum/loader.enum';
import { SystemSettingsHttpService } from 'libs/core/src/lib/http/system-settings.http.service';
import { P24BnpSettings } from 'libs/core/src/lib/model/view-model/payment/p24-bnp-settings.model';
import { BasketDataProvider } from 'libs/core/src/lib/data-provider/basket.data-provider';

declare var Przelewy24CardTokenization: any | undefined;

export enum PayProvider {
  GOOGLE = 'GooglePay',
  APPLE = 'ApplePay'
}

export enum Przelewy24PayWithProvider {
  GOOGLE = 'Przelewy24PayWithGoogle',
  APPLE = 'Przelewy24ApplePay'
}

const P24_CARD_TOKENIZATION_FORM_PATH = '/assets/p24-card-tokenization-form-pl.json';
const P24_BNP_CARD_METHOD_ID_SUFFIX = '_BNP_CARD_FILM';
const P24_BNP_CARD_METHOD_NAME = '20% zniżki na bilety z Kartą Visa Filmowa';
const P24_SHARED_CARD_CONTAINER_SELECTOR = '#creditcardbox-shared';

@UntilDestroy()
@Component({
  selector: 'app-payment-provider-p24-component',
  templateUrl: './p24-payment-provider.component.html',
  standalone: false
})
export class P24PaymentProviderComponent implements PaymentProviderComponentInterface, AfterViewInit, OnDestroy {
  @Output()
  public events: EventEmitter<PaymentProviderEvent> = new EventEmitter<PaymentProviderEvent>();
  @Output()
  public formValidChange = new EventEmitter<boolean>();
  @Output()
  public cardMetadataChange = new EventEmitter<P24CardMetadata>();

  private _messageListener: (event: MessageEvent) => void;

  public waitSecondsForTransactionStep = 7;
  public cardWaitSecondsForTransactionStep = 4;

  public paymentMethod = PaymentMethodEnum;
  public readonly paymentProviderIdentifier: string = ProviderEnum.P24;

  public availablePaymentMethods: {
    pbl: PaymentProviderPayMethodViewModel[];
    card: PaymentProviderPayMethodViewModel;
    cardBnp: PaymentProviderPayMethodViewModel;
    cardToken: PaymentProviderPayMethodViewModel[];
    blik: PaymentProviderPayMethodViewModel;
    blikT6: PaymentProviderPayMethodViewModel;
    blikToken: PaymentProviderPayMethodViewModel;
    paypo: PaymentProviderPayMethodViewModel;
    apay: PaymentProviderPayMethodViewModel;
    gpay: PaymentProviderPayMethodViewModel;
  } = {
    pbl: [],
    card: null,
    cardBnp: null,
    cardToken: [],
    blik: null,
    blikT6: null,
    blikToken: null,
    paypo: null,
    apay: null,
    gpay: null
  };

  public paymentProviderPayMethodCollection: Array<PaymentProviderPayMethodViewModel> = new Array<PaymentProviderPayMethodViewModel>();

  public form: FormGroup;
  public blikFormGroup: FormGroup;
  public cardWithTokenizeFormGroup: FormGroup;
  public paypoFormGroup: FormGroup;

  public formSubmitAttempt = false;

  public paymentProviderConfig: PaymentConfigViewModel | null;
  private paymentProviderInitSubscription: Subscription = Subscription.EMPTY;

  private order: OrderViewModel = null;
  private sessionId: string = null;

  private readonly paymentChannel: string | null = null;

  private continueUrl: string;

  private timeSign = DateTime.now().toMillis();

  token: string | null = null;
  sign: string | null = null;

  P24: any;

  creditCardFormIsReadyToTokenize = false;
  creditCardFormIsLoaded = false;
  showSharedCardForm = true;

  bnpSettings: P24BnpSettings | null = null;

  private _cardTokenizationSubject: Subject<P24MessageData> = new Subject<P24MessageData>();
  private _cardTokenization$: Observable<P24MessageData> = this._cardTokenizationSubject.asObservable();

  private cardTokenizationIframeOptions: any;
  private cardTokenizationRenderRequestId = 0;
  private lastRenderedCardMethodId: string | null = null;
  private bnpPromotionInProgress = false;

  public constructor(
    @Inject(ENVIRONMENT_TOKEN) protected environment: any,
    public paymentProviderStateService: PaymentProviderStateService,
    private orderDataProvider: OrderDataProvider,
    private orderStateService: OrderStateService,
    private applePayService: ApplePayService,
    private googlePayService: GooglePayService,
    private paymentHelperService: PaymentHelperService,
    private route: ActivatedRoute,
    public authStateService: AuthStateService,
    private loggerService: LoggerService,
    private loaderService: LoaderService,
    private translateService: TranslateService,
    private systemSettingsHttpService: SystemSettingsHttpService,
    private basketDataProvider: BasketDataProvider
  ) {
    this.paymentChannel = environment.constants.paymentChannel;
    this.clearPaymentProviderService();
  }

  private clearPaymentProviderService() {
    this.paymentProviderStateService.clearState();
    this.paymentProviderStateService.selectedMethod = null;
    this.paymentProviderStateService.selectedPayMethodTypeCustom = null;
    this.paymentProviderStateService.selectedProvider = null;
  }

  private fetchConfig() {
    fetch(makeUrl(this.environment, P24_CARD_TOKENIZATION_FORM_PATH))
      .then((response: Response) => response.json())
      .then((config) => (this.cardTokenizationIframeOptions = config));
  }

  public ngOnInit() {
    this.paymentProviderStateService.state$.pipe(untilDestroyed(this)).subscribe((state) => {
      if (!!state) {
        if (
          state.type !== this.paymentProviderStateService.selectedPayMethodTypeCustom ||
          state.payByLink?.id !== this.paymentProviderStateService.selectedMethod?.id
        ) {
          this.paymentProviderStateService.selectedPayMethodTypeCustom = state.type;

          if (state.type === PaymentMethodEnum.BLIK_T6) {
            if (this.availablePaymentMethods.blikToken && this.paymentProviderStateService.selectedMethod === this.availablePaymentMethods.blikToken) {
              this.paymentProviderStateService.selectedMethod = this.availablePaymentMethods.blikToken;
              this.emitFormState(null);
            } else {
              this.paymentProviderStateService.selectedMethod = this.availablePaymentMethods.blikT6;
              this.emitFormState(this.blikFormGroup);
            }
          } else if (state.type === PaymentMethodEnum.BLIK) {
            this.paymentProviderStateService.selectedMethod = this.availablePaymentMethods.blik;
            this.emitFormState(this.blikFormGroup);
          } else if (state.type === PaymentMethodEnum.PAY_PO) {
            this.paymentProviderStateService.selectedMethod = this.availablePaymentMethods.paypo;
            this.emitFormState(this.paypoFormGroup);
          } else if (state.type === PaymentMethodEnum.CREDIT_CARD) {
            //TOKENIZACJA I OBSŁUGA PŁATNOŚCI KARTĄ PRZEZ P24 w IFRAME
            this.paymentProviderStateService.selectedMethod = state.payByLink;
            this.emitFormState(this.cardWithTokenizeFormGroup);
            this.registerCardTokenizationIframe();
          } else {
            this.paymentProviderStateService.selectedMethod = state.payByLink;
            if (state.type === PaymentMethodEnum.PBL) {
              this.emitFormState(this.form, true);
            } else {
              this.emitFormState(null);
            }
          }
        } else {
          if (state.type === PaymentMethodEnum.CREDIT_CARD) {
            // Keep card method selected on duplicate CREDIT_CARD state emissions.
            this.paymentProviderStateService.selectedPayMethodTypeCustom = PaymentMethodEnum.CREDIT_CARD;
            this.paymentProviderStateService.selectedMethod = state.payByLink ?? this.paymentProviderStateService.selectedMethod;
            this.emitFormState(this.cardWithTokenizeFormGroup);
          } else {
            this.paymentProviderStateService.selectedPayMethodTypeCustom = null;
            this.paymentProviderStateService.selectedMethod = null;
            this.emitFormState(null);
          }
        }
      }

      const isBnpAvailable = !!this.availablePaymentMethods.cardBnp;
      if (isBnpAvailable) {
        const promotionId = this.bnpSettings?.pid;
        const desiredBnpPromotionState = this.resolveDesiredBnpPromotionState(state);
        const isPromotionAppliedInOrder = this.orderStateService.hasOrderPromotionById(promotionId);

        if (promotionId && desiredBnpPromotionState !== null && isPromotionAppliedInOrder !== desiredBnpPromotionState) {
          this.syncBnpPromotion(desiredBnpPromotionState);
        }
      }
    });

    this.paymentProviderStateService.action$.pipe(untilDestroyed(this)).subscribe((action) => {
      if (action === PaymentProviderActionType.ResetPaymentProvider) {
        this.initialComponent();
      }
    });

    this.fetchConfig();
  }

  public ngAfterViewInit(): void {
    this.initialComponent();
  }

  private initialComponent() {
    this._messageListener = (event: MessageEvent) => this.receiveMessage(event);
    window.addEventListener('message', this._messageListener, false);

    this.orderStateService.state$.pipe(untilDestroyed(this)).subscribe((order) => (this.order = order));

    this.loadBnpSettings();

    this.events.next(new WorkPaymentProviderEvent());

    this.form = this.paymentProviderStateService.getPrzelewy24RulesFormGroup();
    this.blikFormGroup = this.paymentProviderStateService.getPrzelewy24BlikFormGroup();
    this.cardWithTokenizeFormGroup = this.paymentProviderStateService.getPrzelewy24CardWithTokenizeGroup();
    this.paypoFormGroup = this.paymentProviderStateService.getPrzelewy24PayPoFormGroup();

    this.watchFormValidation();

    this.initPaymentProviderWidget();
  }

  private formSubscriptions: Subscription[] = [];
  public watchFormValidation() {
    this.formSubscriptions.forEach((sub) => sub.unsubscribe());
    this.formSubscriptions = [];
    const forms = [
      { type: PaymentMethodEnum.PBL, form: this.form, verify: true },
      { type: PaymentMethodEnum.BLIK_T6, form: this.blikFormGroup, verify: false },
      { type: PaymentMethodEnum.CREDIT_CARD, form: this.cardWithTokenizeFormGroup, verify: false },
      { type: PaymentMethodEnum.PAY_PO, form: this.paypoFormGroup, verify: false }
    ];

    forms.forEach(({ type, form, verify }) => {
      const sub = form.statusChanges.subscribe(() => {
        if (this.paymentProviderStateService.selectedPayMethodTypeCustom === type) {
          this.emitFormState(form, verify);
        }
      });
      this.formSubscriptions.push(sub);
    });
  }

  emitFormState(currentForm: FormGroup | null, verify = false) {
    const valid = (currentForm ? currentForm.valid : true) && (!verify || !!this.paymentProviderStateService.selectedMethod);
    this.formValidChange.emit(valid);
  }

  public ngOnDestroy(): void {
    this.cardTokenizationRenderRequestId += 1;
    this.lastRenderedCardMethodId = null;

    if (this.paymentProviderInitSubscription !== Subscription.EMPTY) {
      this.paymentProviderInitSubscription.unsubscribe();
      this.paymentProviderInitSubscription = Subscription.EMPTY;
    }

    this.form.reset();

    window.removeEventListener('message', this._messageListener);
  }

  private getLocalPaymentRedirectUrl() {
    const request: CreatePaymentRedirectUrl = new CreatePaymentRedirectUrl();
    request.paymentChannel = this.paymentChannel;
    request.order = this.order;
    request.route = this.route;
    return request;
  }

  public onPostInitPayment(paymentModel: PaymentViewModel): void {
    if (this.canUsePaymentMethod(PaymentMethodEnum.PBL, PaymentMethodEnum.PAY_PO, PaymentMethodEnum.CREDIT_CARD, PaymentMethodEnum.CREDIT_CARD_TOKEN)) {
      this.redirect(paymentModel.plainPayload);
      return;
    }

    if (this.canUsePaymentMethod(PaymentMethodEnum.GPAY)) {
      this.payWith(PayProvider.GOOGLE, paymentModel);
    }

    if (this.canUsePaymentMethod(PaymentMethodEnum.APAY)) {
      this.payWith(PayProvider.APPLE, paymentModel);
    }

    if (this.canUsePaymentMethod(PaymentMethodEnum.BLIK)) {
      const waitingTime: number = this.waitSecondsForTransactionStep;
      this.events.next(new WaitPaymentProviderEvent(waitingTime));

      setTimeout(() => {
        if (paymentModel.type === PaymentMethodEnum.BLIK) {
          this.redirect(paymentModel.plainPayload);
        } else {
          this.redirect(this.paymentHelperService.createPaymentRedirectUrl(this.getLocalPaymentRedirectUrl(), this.continueUrl));
        }
      }, 300);
    }
  }

  public onPreInitPayment(event: OrderPaymentPackage): Observable<OrderPaymentPackage> {
    this.continueUrl = event.continueUrl;

    return new Observable<OrderPaymentPackage>((subscriber) => {
      if (this.canUsePaymentMethod(PaymentMethodEnum.PBL)) {
        if (!this.form.valid || !this.form.get('regulationAccept').value) {
          event.abort = true;
          this.emitNotAcceptedProviderRulesEvent();
        } else if (!this.paymentProviderStateService.selectedMethod) {
          event.abort = true;
          this.emitNotSelectedPaymentProviderEvent();
        } else {
          event.intPayMethodType = this.paymentProviderStateService.selectedMethod.type;
          event.intPayMethodValue = this.paymentProviderStateService.selectedMethod.id;
          event.regulationAccept = true;
        }

        subscriber.next(event);
        subscriber.complete();
        return;
      }

      if (this.canUsePaymentMethod(PaymentMethodEnum.PAY_PO)) {
        if (!this.paypoFormGroup.valid || !this.paypoFormGroup.get('regulationAccept').value) {
          event.abort = true;
        } else if (!this.paymentProviderStateService.selectedMethod) {
          event.abort = true;
          this.emitNotSelectedPaymentProviderEvent();
        } else {
          event.intPayMethodType = this.paymentProviderStateService.selectedMethod.type;
          event.intPayMethodValue = this.paymentProviderStateService.selectedMethod.id;
          event.clientAddress = this.paypoFormGroup.get('clientAddress').value;
          event.regulationAccept = true;
        }

        subscriber.next(event);
        subscriber.complete();
        return;
      }

      if (this.canUsePaymentMethod(PaymentMethodEnum.BLIK)) {
        if (this.paymentProviderStateService.selectedMethod.type === PaymentMethodEnum.BLIK_TOKEN) {
          event.intPayMethodType = this.paymentProviderStateService.selectedMethod.type;
          event.intPayMethodValue = this.paymentProviderStateService.selectedMethod.token;
        } else if (this.paymentProviderStateService.selectedMethod.type === PaymentMethodEnum.BLIK) {
          event.intPayMethodType = this.paymentProviderStateService.selectedMethod.type;
          event.intPayMethodValue = this.paymentProviderStateService.selectedMethod.id;
        } else if (this.paymentProviderStateService.selectedMethod.type === PaymentMethodEnum.BLIK_T6) {
          this.formSubmitAttempt = true;
          if (this.blikFormGroup.valid) {
            const blikCodeValue = this.blikFormGroup.get('blikCode').value;
            const filteredInputBlikCode: string | null = sanitizeInput(blikCodeValue);
            event.intPayMethodType = this.paymentProviderStateService.selectedMethod.type;
            event.intPayMethodValue = filteredInputBlikCode;
            event.saveToken = this.authStateService.isLogged();
          } else {
            event.abort = true;
          }
        }

        subscriber.next(event);
        subscriber.complete();
        return;
      }

      if (this.canUsePaymentMethod(PaymentMethodEnum.CREDIT_CARD)) {
        if (this.paymentProviderStateService.selectedTokenCardMethod !== null && !this.isBnpCardMethodSelected()) {
          event.intPayMethodType = this.paymentProviderStateService.selectedTokenCardMethod.type;
          event.intPayMethodValue = this.paymentProviderStateService.selectedTokenCardMethod.token;
          subscriber.next(event);
          subscriber.complete();
        } else if (this.creditCardFormIsReadyToTokenize) {
          const saveToken = this.canUseSavedCardOptions && Boolean(Number(this.cardWithTokenizeFormGroup.get('saveToken').value));
          this.cardTokenizationPromise(saveToken ? 'permanent' : 'temporary')
            .then(async (data) => {
              const cardBin = this.extractCardBin(data);
              const cardHasBnpBin = cardBin === this.bnpSettings?.bin;

              if (this.isBnpCardMethodSelected() && !cardHasBnpBin) {
                event.abort = true;
                this.emitIncorrectDataCreditCardFormEvent();
                this.reinitializeBnpCardForm();
                subscriber.next(event);
                subscriber.complete();
                return;
              }

              event.intPayMethodType = PaymentMethodEnum.CREDIT_CARD_TOKEN;
              event.intPayMethodValue = data.refId;
              event.saveToken = saveToken;

              subscriber.next(event);
              subscriber.complete();
            })
            .catch((error) => this.handleError(event, error, subscriber));
        } else {
          this.emitIncorrectDataCreditCardFormEvent();
          event.abort = true;
          subscriber.next(event);
          subscriber.complete();
        }

        return;
      }

      if (this.canUsePaymentMethod(PaymentMethodEnum.GPAY)) {
        const handleGooglePay = () => {
          this.handleProcessForGooglePay(event, subscriber);
        };

        this.logAndGo(`Pre init payment by ${PayProvider.GOOGLE}`, handleGooglePay);
        return;
      }

      if (this.canUsePaymentMethod(PaymentMethodEnum.APAY)) {
        this.handleProcessForApplePay(event, subscriber);
        return;
      }

      event.abort = true;
      this.emitNotSelectedPaymentProviderEvent();
      subscriber.next(event);
      subscriber.complete();
    });
  }

  public onClickPaymentMethod(method: PaymentProviderPayMethodViewModel): void {
    const state = this.paymentProviderStateService.getState();
    this.paymentProviderStateService.setState({ ...state, payByLink: method });
  }

  public onClickTokenCardMethod(method: PaymentProviderPayMethodViewModel): void {
    this.paymentProviderStateService.selectedTokenCardMethod = this.paymentProviderStateService.selectedTokenCardMethod !== method ? method : null;
  }

  get canShowToggleButtonForBlik() {
    return !!this.availablePaymentMethods.blikToken && !!this.availablePaymentMethods.blikT6;
  }

  public onToggleBlikMethod(): void {
    const selectedMethodType = this.paymentProviderStateService.selectedMethod?.type;
    if (!selectedMethodType || !this.canShowToggleButtonForBlik) {
      return;
    }

    if (selectedMethodType === this.availablePaymentMethods.blikT6.type) {
      this.paymentProviderStateService.selectedMethod = this.availablePaymentMethods.blikToken;
      this.blikFormGroup.get('withCode').setValue(0);
      this.blikFormGroup.get('blikCode').setValue('');
    } else if (selectedMethodType === this.availablePaymentMethods.blikToken.type) {
      this.paymentProviderStateService.selectedMethod = this.availablePaymentMethods.blikT6;
      this.blikFormGroup.get('withCode').setValue(1);
    }
  }

  private initPaymentProviderWidget(): void {
    const requestModel = new OrderPaymentRequestModel();
    requestModel.channel = this.paymentChannel;

    const orderPaymentPackage = new OrderPaymentPackage();
    orderPaymentPackage.cinemaId = this.order.cinemaId;
    orderPaymentPackage.orderId = this.order.id;
    orderPaymentPackage.paymentProviderIdentifier = this.paymentProviderIdentifier;
    orderPaymentPackage.requestModel = requestModel;

    this.paymentProviderInitSubscription = forkJoin({
      config: this.orderDataProvider.getPaymentProviderConfig(orderPaymentPackage),
      methods: this.orderDataProvider.getPaymentProviderPayMethodCollection(orderPaymentPackage)
    }).subscribe({
      next: (forkData) => {
        const methods = forkData.methods.filter((method) => method.status === 'ENABLED');
        this.paymentProviderConfig = forkData.config;
        this.paymentProviderPayMethodCollection = methods;

        this.availablePaymentMethods.card = methods.find((x) => x.type === PaymentMethodEnum.CREDIT_CARD) ?? null;
        this.availablePaymentMethods.cardBnp = this.canDisplayBnpCardMethod ? this.createBnpCardMethod(this.availablePaymentMethods.card) : null;
        this.availablePaymentMethods.cardToken = methods.filter((x) => x.type === PaymentMethodEnum.CREDIT_CARD_TOKEN);

        this.availablePaymentMethods.pbl = methods.filter((x) => x.type === PaymentMethodEnum.PBL);
        this.availablePaymentMethods.paypo = methods.find((x) => x.type === PaymentMethodEnum.PAY_PO);

        this.availablePaymentMethods.blik = methods.find((x) => x.type === PaymentMethodEnum.BLIK) ?? null;
        this.availablePaymentMethods.blikT6 = methods.find((x) => x.type === PaymentMethodEnum.BLIK_T6) ?? null;
        this.availablePaymentMethods.blikToken = methods.find((x) => x.type === PaymentMethodEnum.BLIK_TOKEN) ?? null;

        const applePayMethod = methods.find((x) => x.type === PaymentMethodEnum.APAY) ?? null;
        if (applePayMethod) {
          this.applePayService.init(this.paymentProviderConfig.appleMerchantId);
          this.availablePaymentMethods.apay = applePayMethod;
        }

        this.verifyGooglePayAvailability(methods.find((x) => x.type === PaymentMethodEnum.GPAY) ?? null);
        this.events.next(new DonePaymentProviderEvent());
      },
      error: (e: HttpErrorResponse) => this.events.next(new ErrorPaymentProviderEvent(e))
    });
  }

  get getDefaultPaymentImageUrl() {
    return `/assets/images/payment-providers/Przelewy24_logo.png`;
  }

  addTimeSign(url) {
    return `${url}?t=${this.timeSign}`;
  }

  public onSelectCreditCardMethod(method: PaymentProviderPayMethodViewModel): void {
    if (method?.id === this.availablePaymentMethods.cardBnp?.id) {
      this.paymentProviderStateService.selectedTokenCardMethod = null;
      this.cardWithTokenizeFormGroup?.get('saveToken')?.setValue(0, { emitEvent: false });
    }

    const state = this.paymentProviderStateService.getState();
    this.paymentProviderStateService.setState({ ...state, payByLink: method });
  }

  private get selectedCardMethodIdForView(): string | null {
    return this.paymentProviderStateService.selectedMethod?.id ?? this.paymentProviderStateService.getState()?.payByLink?.id ?? null;
  }

  public get isDefaultCardMethodSelected(): boolean {
    const selectedCardMethodId = this.selectedCardMethodIdForView;

    return (
      this.paymentProviderStateService.selectedPayMethodTypeCustom === PaymentMethodEnum.CREDIT_CARD &&
      selectedCardMethodId !== this.availablePaymentMethods.cardBnp?.id
    );
  }

  public get isBnpCardMethodSelectedForView(): boolean {
    const selectedCardMethodId = this.selectedCardMethodIdForView;

    return (
      this.paymentProviderStateService.selectedPayMethodTypeCustom === PaymentMethodEnum.CREDIT_CARD &&
      selectedCardMethodId === this.availablePaymentMethods.cardBnp?.id
    );
  }

  public get canUseSavedCardOptions(): boolean {
    return !this.isBnpCardMethodSelectedForView;
  }

  public get canDisplayBnpCardMethod(): boolean {
    return (
      !!this.bnpSettings?.tids && this.orderStateService.hasOrderTicketByIds(this.bnpSettings.tids) && this.hasTodayBnpTicketInBasket(this.bnpSettings.tids)
    );
  }

  private hasTodayBnpTicketInBasket(ticketIds: string[] | null | undefined): boolean {
    if (!ticketIds || ticketIds.length === 0) {
      return false;
    }

    const normalizedTicketIds = ticketIds.map((id) => `${id}`.toLowerCase());
    const now = DateTime.now();
    const screeningItems = this.order?.screeningItems ?? [];
    const basketPageModel = this.basketDataProvider.basketPageModel;

    return screeningItems.some((item) => {
      const ticketId = `${item?.ticketId ?? ''}`.toLowerCase();
      const isSameScreening = item?.screeningId.toLowerCase() === basketPageModel?.screening?.id?.toLowerCase();
      const screeningTimeFrom = isSameScreening ? basketPageModel?.screening?.screeningTimeFrom : null;

      return normalizedTicketIds.includes(ticketId) && !!screeningTimeFrom && screeningTimeFrom.hasSame(now, 'day');
    });
  }

  private canUsePaymentMethod(...params: PaymentMethodEnum[]) {
    return params.some((method) => this.paymentProviderStateService.selectedPayMethodTypeCustom?.includes(method));
  }

  private verifyGooglePayAvailability(method) {
    const merchantId = this.environment.paymentManager.przelewy24?.googlePayMerchantId;
    if (merchantId && method && !MobileDeviceUtils.isSafari()) {
      this.availablePaymentMethods.gpay = method;
    } else {
      console.info('Google Pay is not available: missing merchant ID or method configuration.');
    }
  }

  private handleProcessForGooglePay(event: any, subscriber: any): void {
    const merchantId = this.environment.paymentManager.przelewy24?.googlePayMerchantId;
    if (!merchantId) {
      this.handleError(event, 'No Google Pay merchantID.', subscriber);
      return;
    }

    const googlePayClient: GooglePay.PaymentsClient = this.googlePayService.getClient(this.paymentProviderConfig.environment);
    const orderPrice: number = this.order.valueToPay;

    const gpayRequestData = new CreatePaymentDataRequestDto();

    gpayRequestData.gateway = this.paymentProviderIdentifier;
    gpayRequestData.gatewayMerchantId = this.paymentProviderConfig.merchantId;

    gpayRequestData.merchantId = merchantId;

    gpayRequestData.totalPriceStatus = PaymentTotalPriceStatus.FINAL;
    gpayRequestData.totalPrice = FinancialHelper.fixedValue(orderPrice);
    gpayRequestData.currencyCode = this.environment.constants.currency;
    gpayRequestData.countryCode = this.environment.constants.country;
    gpayRequestData.allowedCardAuthMethods = [PaymentAuthMethod.PAN_ONLY, PaymentAuthMethod.CRYPTOGRAM_3DS];
    gpayRequestData.allowedCardMethods = [PaymentCardNetwork.VISA, PaymentCardNetwork.MASTERCARD];

    const paymentDataRequest: PaymentDataModel = this.googlePayService.createPaymentDataRequest(gpayRequestData);

    const callLoadPaymentData = () => {
      googlePayClient
        .loadPaymentData(paymentDataRequest as any)
        .then((paymentData: PaymentData) => {
          const afterTokenization = () => {
            const token: string | null = paymentData?.paymentMethodData?.tokenizationData?.token ?? null;
            event.intPayMethodType = PaymentMethodEnum.GPAY;
            event.intPayMethodValue = btoa(token as string);
            subscriber.next(event);
            subscriber.complete();
          };

          this.logAndGo(`${PayProvider.GOOGLE}: complete tokenization`, afterTokenization);
        })
        .catch((error) => this.handleError(event, error, subscriber));
    };

    this.logAndGo(`${PayProvider.GOOGLE}: loadPaymentData()`, callLoadPaymentData);
  }

  private handleProcessForApplePay(event: any, subscriber: any): void {
    this.applePayService.startPayment(
      (merchantEvent, session) => {
        this.orderDataProvider.getApplePaySession(this.order.cinemaId, this.order.id, this.paymentProviderIdentifier).subscribe({
          next: (merchantSession) => {
            session.completeMerchantValidation(merchantSession);
          },
          error: (error: any) => {
            session.abort();
            this.abortPaymentProcess();
            this.handleError(event, 'Merchant validation failed.', subscriber);
          }
        });
      },
      (token) => {
        event.intPayMethodType = PaymentMethodEnum.APAY;
        event.intPayMethodValue = btoa(token as string);
        subscriber.next(event);
        subscriber.complete();
      },
      () => {
        this.abortPaymentProcess();
        this.handleError(event, 'Apple Pay session aborted.', subscriber);
      }
    );
  }

  private cardTokenizationPromise(mode: 'permanent' | 'temporary'): Promise<P24TokenizationData> {
    return new Promise((resolve, reject) => {
      this._cardTokenization$.pipe(first()).subscribe({
        next: (message: P24MessageData) => {
          if (message.type === 'success') {
            resolve(message.data);
          } else {
            console.error(message.errors);
            reject(new Error(message.errors[0]));
          }
        },
        error: (err) => {
          reject(err);
        }
      });

      this.P24.tokenize(mode);
    });
  }

  private registerCardTokenizationIframe(forceRecreate = false) {
    const selectedCardMethodId = this.selectedCardMethodIdForView;
    const shouldReuseExistingIframe =
      !forceRecreate && this.hasRenderedCardTokenizationIframe() && !!selectedCardMethodId && selectedCardMethodId === this.lastRenderedCardMethodId;

    if (shouldReuseExistingIframe) {
      this.creditCardFormIsLoaded = true;
      return;
    }

    this.creditCardFormIsLoaded = false;
    this.cardTokenizationRenderRequestId += 1;

    const data = this.getCardTokenizationData();
    const src = this.getCardTokenizationScriptUrl();

    this.loadCardTokenizationScript(src, data, 0, this.cardTokenizationRenderRequestId, selectedCardMethodId);
  }

  private getCardTokenizationData() {
    return {
      merchantId: +this.paymentProviderConfig.merchantId,
      sessionId: this.order.id,
      sign: this.paymentProviderConfig.providerConfiguration.sign
    };
  }

  private getCardTokenizationScriptUrl() {
    return `https://${isDevMode() ? 'sandbox' : 'secure'}.przelewy24.pl/js/cardTokenizationIframe.min.js`;
  }

  private loadCardTokenizationScript(src: string, data: any, attempt: number, requestId: number, selectedCardMethodId: string | null) {
    setTimeout(() => {
      loadScript('przelewy24-tokenization', src, () => {
        if (requestId !== this.cardTokenizationRenderRequestId) {
          return;
        }

        const containerSelector = this.resolveCardTokenizationContainerSelector();
        const tokenizationConstructor = this.resolveP24TokenizationConstructor();

        if (!containerSelector || !tokenizationConstructor) {
          if (attempt < 10) {
            setTimeout(() => this.loadCardTokenizationScript(src, data, attempt + 1, requestId, selectedCardMethodId), 50);
          }
          return;
        }

        this.P24 = new tokenizationConstructor(data.merchantId, data.sessionId, data.sign);
        this.lastRenderedCardMethodId = selectedCardMethodId;

        const user = this.authStateService.getUser();
        const psu = user ? { email: user.email } : {};

        const iframeOptions = {
          ...this.cardTokenizationIframeOptions,
          title: this.translateService.instant('payment.provider.p24.card.form-title')
          // psu: psu
        };

        this.P24.render('form', containerSelector, iframeOptions);

        setTimeout(() => {
          const iframe = document.querySelector(`${containerSelector} iframe`) as HTMLIFrameElement;
          if (iframe) {
            iframe.setAttribute('title', iframeOptions.title);
          }
        }, 100);
      });
    });
  }

  private resolveP24TokenizationConstructor(): any {
    const constructorFromGlobal = typeof Przelewy24CardTokenization !== 'undefined' ? Przelewy24CardTokenization : (window as any)?.Przelewy24CardTokenization;

    return typeof constructorFromGlobal === 'function' ? constructorFromGlobal : null;
  }

  private resolveCardTokenizationContainerSelector(): string | null {
    return document.querySelector(P24_SHARED_CARD_CONTAINER_SELECTOR) ? P24_SHARED_CARD_CONTAINER_SELECTOR : null;
  }

  private hasRenderedCardTokenizationIframe(): boolean {
    return Boolean(document.querySelector(`${P24_SHARED_CARD_CONTAINER_SELECTOR} iframe`));
  }

  receiveMessage(event: MessageEvent) {
    console.log('P24 message event', {
      origin: event.origin,
      data: event.data
    });

    const messageData = event?.data as P24MessageData;

    if (!messageData?.p24 || this.paymentProviderStateService.selectedPayMethodTypeCustom !== PaymentMethodEnum.CREDIT_CARD) {
      return;
    }

    const handlers = {
      start: () => {
        this.creditCardFormIsLoaded = true;
        this.creditCardFormIsReadyToTokenize = false;
      },
      ready: () => {
        this.creditCardFormIsReadyToTokenize = event.data.status;
      },
      success: () => {
        if (messageData.data) {
          this.cardMetadataChange.emit(this.mapToCardMetadata(messageData.data));
        }
        this._cardTokenizationSubject.next(messageData);
      },
      fail: () => this._cardTokenizationSubject.next(messageData)
    };

    const handler = handlers[messageData.type];
    if (handler) {
      handler();
    }
  }

  private mapToCardMetadata(data: P24TokenizationData): P24CardMetadata {
    const bin = Number.isFinite(data?.bin) ? String(data.bin).padStart(6, '0').slice(0, 6) : null;
    const suffix = data?.mask?.match(/(\d{4})(?!.*\d)/)?.[1] ?? null;

    return {
      prefix: bin,
      suffix,
      mask: data?.mask ?? null,
      brand: data?.cardType ?? null,
      bin,
      expiration: data?.cardDate ?? null,
      country: data?.cardCountry ?? null,
      tokenization: data?.tokenization ?? null,
      refId: data?.refId ?? null
    };
  }

  private emitNotSelectedPaymentProviderEvent() {
    this.events.emit(new ErrorPaymentKeyProviderEvent(generalPaymentError.NotSelectedPaymentProvider));
  }

  private emitNotAcceptedProviderRulesEvent() {
    this.events.emit(new ErrorPaymentKeyProviderEvent(przelewy24PaymentError.NotAcceptedProviderRules));
  }

  private emitIncorrectDataCreditCardFormEvent() {
    this.events.emit(new ErrorPaymentKeyProviderEvent(przelewy24PaymentError.IncorrectDataCreditCardForm));
  }

  private isBnpCardMethodSelected(): boolean {
    return this.paymentProviderStateService.selectedMethod?.id === this.availablePaymentMethods.cardBnp?.id;
  }

  private isCardTokenizedFlowForMethod(methodId: string | null | undefined): boolean {
    return (
      this.paymentProviderStateService.selectedPayMethodTypeCustom === PaymentMethodEnum.CREDIT_CARD_TOKEN &&
      !!methodId &&
      this.paymentProviderStateService.selectedMethod?.id === methodId
    );
  }

  private extractCardBin(data: P24TokenizationData): string | null {
    return Number.isFinite(data?.bin) ? String(data.bin).padStart(6, '0').slice(0, 6) : null;
  }

  private createBnpCardMethod(cardMethod: PaymentProviderPayMethodViewModel | null): PaymentProviderPayMethodViewModel | null {
    if (!cardMethod) {
      return null;
    }

    return Object.assign(new PaymentProviderPayMethodViewModel(), cardMethod, {
      id: `${cardMethod.id}${P24_BNP_CARD_METHOD_ID_SUFFIX}`,
      name: P24_BNP_CARD_METHOD_NAME,
      imageUrl: 'https://static.przelewy24.pl/payment-form-logo/021/img.svg'
    });
  }

  private reinitializeBnpCardForm(): void {
    const bnpMethod = this.availablePaymentMethods.cardBnp;
    if (!bnpMethod) {
      this.rerenderSharedCardFormAndInit();
      return;
    }

    // Keep BNP card selected and reinitialize full iframe form to clear all entered card fields.
    this.paymentProviderStateService.selectedPayMethodTypeCustom = PaymentMethodEnum.CREDIT_CARD;
    this.paymentProviderStateService.selectedMethod = bnpMethod;
    this.paymentProviderStateService.selectedTokenCardMethod = null;

    const state = this.paymentProviderStateService.getState();
    this.paymentProviderStateService.setState({
      ...state,
      type: PaymentMethodEnum.CREDIT_CARD,
      provider: this.paymentProviderIdentifier,
      payByLink: bnpMethod
    });

    this.rerenderSharedCardFormAndInit();
  }

  private rerenderSharedCardFormAndInit(): void {
    this.showSharedCardForm = false;
    this.cardTokenizationRenderRequestId += 1;
    this.P24 = null;
    this.lastRenderedCardMethodId = null;
    this.creditCardFormIsLoaded = false;
    this.creditCardFormIsReadyToTokenize = false;

    setTimeout(() => {
      this.showSharedCardForm = true;
      this.registerCardTokenizationIframe(true);
    }, 0);
  }

  private syncBnpPromotion(shouldApplyPromotion: boolean): void {
    if (this.bnpPromotionInProgress) {
      return;
    }

    const promotionId = this.bnpSettings?.pid;
    const cinemaId = this.order?.cinemaId;
    const orderId = this.order?.id;
    if (!promotionId || !cinemaId || !orderId) {
      return;
    }

    this.bnpPromotionInProgress = true;
    this.loaderService.showLoader(LoaderEnum.PAYMENT);

    const request$ = shouldApplyPromotion
      ? this.orderDataProvider.putPromotionToOrder(cinemaId, orderId, promotionId)
      : this.orderDataProvider.deletePromotionFromOrder(cinemaId, orderId, promotionId);

    request$
      .pipe(
        first(),
        finalize(() => {
          this.bnpPromotionInProgress = false;
          this.loaderService.hideLoader(LoaderEnum.PAYMENT);
        })
      )
      .subscribe({
        next: (order) => {
          this.orderStateService.setOrder(order);
        },
        error: (error) => {
          this.loggerService.log('P24 BNP promotion sync failed', { error });
        }
      });
  }

  private resolveDesiredBnpPromotionState(state: any): boolean | null {
    if (!state?.type) {
      // No payment method selected yet (e.g. after refresh) - leave promotion as-is.
      return null;
    }

    if (state.type !== PaymentMethodEnum.CREDIT_CARD) {
      // Any selected non-BNP method should remove BNP promotion.
      return false;
    }

    const selectedCardMethodId = state.payByLink?.id ?? this.paymentProviderStateService.selectedMethod?.id ?? null;
    if (!selectedCardMethodId) {
      // CREDIT_CARD state can be emitted before payByLink is set - defer promotion sync.
      return null;
    }

    return selectedCardMethodId === this.availablePaymentMethods.cardBnp?.id;
  }

  private abortPaymentProcess() {
    this.events.next(new DonePaymentProviderEvent());
  }

  private payWith(payProvider: PayProvider, paymentModel: PaymentViewModel) {
    const providerSignature =
      payProvider === PayProvider.GOOGLE ? Przelewy24PayWithProvider.GOOGLE : payProvider === PayProvider.APPLE ? Przelewy24PayWithProvider.APPLE : undefined;
    if (!providerSignature) {
      return;
    }

    const payWithProvider = () => {
      const scriptUrl = this.getScriptUrlForPayWithProvider(providerSignature, paymentModel.id);
      loadScriptPromise(scriptUrl, false)
        .then(() => {
          if (window[providerSignature]) {
            window[providerSignature].config({
              errorCallback: () => this.cancelBasketAndRedirect(`${providerSignature}: errorCallback`),
              exceptionCallback: (e) => this.cancelBasketAndRedirect(`${providerSignature}: exceptionCallback`, e),
              requestFailedCallback: (e, t, n) => this.cancelBasketAndRedirect(`${providerSignature}: requestFailedCallback`, e, t, n),
              completePaymentCallback: () => {
                const completePaymentProcess = () => {
                  this.events.next(new DonePaymentProviderEvent());
                  this.redirect();
                };

                this.logAndGo(`${providerSignature}: completePaymentCallback; Redirect to order summary`, completePaymentProcess);
              }
            });

            const callCharge = () => {
              window[providerSignature].charge();
            };

            this.logAndGo(`${providerSignature}: charge()`, callCharge);
          } else {
            this.cancelBasketAndRedirect(`${providerSignature}: not defined`);
          }
        })
        .catch((error) => {
          this.cancelBasketAndRedirect(`${providerSignature}: script loading failed: `, error);
        });
    };

    this.logAndGo(`Post init payment with ${payProvider} - Load script for ${providerSignature}`, payWithProvider);
  }

  private getScriptUrlForPayWithProvider(provider: Przelewy24PayWithProvider, token: string) {
    const endpoint = provider === Przelewy24PayWithProvider.GOOGLE ? 'payWithGoogle' : 'applepay';
    return `https://${isDevMode() ? 'sandbox' : 'secure'}.przelewy24.pl/bundle/${endpoint}/${token}`;
  }

  private redirect(url: string = null) {
    if (!url) {
      url = this.paymentHelperService.getTargetUrl(this.getLocalPaymentRedirectUrl());
    }

    window.location.href = url;
  }

  private cancelBasketAndRedirect(url: string, message?: any, ...optionalParams: any[]) {
    console.error(message, optionalParams);
    const failedProcess = () => {
      this.orderStateService.removeOrderWithDelete(true);
      setTimeout(() => {
        this.redirect();
      }, 200);
    };

    this.logAndGo(`Failed - ${message}`, failedProcess);
  }

  private handleError(event: any, error: any, subscriber: any): void {
    event.abort = true;
    console.error(error);

    subscriber.next(event);
    subscriber.complete();
  }

  private logAndGo(message: string, callback?: () => void) {
    const logData: { sessionId?: string } = {};
    if (this.order) {
      logData.sessionId = `${this.order.cinemaId}.${this.order.id}`;
    }

    this.loggerService.log(message, logData).subscribe({
      complete: callback
    });
  }

  private loadBnpSettings(): void {
    this.systemSettingsHttpService
      .get()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (systemSettings: any) => {
          if (systemSettings?.restSettings?.length > 0) {
            this.bnpSettings = systemSettings.restSettings[0].bnp as P24BnpSettings;
          }
        },
        error: (error) => {
          this.loggerService.log('Failed to load BNP settings from system/settings', { error });
        }
      });
  }
}
